// app/frontend/app/lib/batchTransactions.ts
//
// React-free twin of MineralInventoryCalculator for the multi-transaction grid.
// The /inventories row is an already-computed per-STR net position (it does not
// expose separate acq/div components), so we treat its net_acres / weighted_nri
// as the PRE-BATCH baseline and layer only the other in-grid rows for the same
// STR on top (order-independent). Keep in lockstep with
// app/services/mineral_inventory_calculator.rb.
import { normalizeStr } from "@/app/lib/str"
import type { RowState } from "@/app/pages/NewTransactionPage/types"

export interface BaseInvRow {
  str: string
  weighted_nri: string
  net_acres: string
  county: string | null
}

export interface EffectivePosition {
  naOwned: number
  nriOwned: number
  orriReserved: number // fraction, for the current DIV row
  hasPosition: boolean
}

const num = (v: string) => (v.trim() === "" ? 0 : Number(v))

// Effective position for a row = pre-batch inventory for the STR PLUS every
// OTHER grid row targeting the same STR. NRI baseline comes from the inventory
// row's weighted_nri (acquisitions never lowered by divestitures); in-grid ACQ
// rows contribute additional weighted NRI, DIV rows only reduce net acres.
export function deriveEffectivePosition(
  base: BaseInvRow | null,
  rows: RowState[],
  thisRowId: string
): EffectivePosition {
  const thisRow = rows.find((r) => r.id === thisRowId)
  const target = normalizeStr(thisRow?.str ?? "")

  const baseNet = base ? Number(base.net_acres) : 0
  const baseNri = base ? Number(base.weighted_nri) : 0

  let netAcres = baseNet
  // Weighted-NRI accumulator: seed with the pre-batch position (baseNri over
  // baseNet acres), then add in-grid ACQ contributions.
  let wNri = baseNri * (baseNet > 0 ? baseNet : 0)
  let acqAcres = baseNet > 0 ? baseNet : 0
  let hadBasePosition = baseNri > 0 || baseNet !== 0

  if (target) {
    for (const r of rows) {
      if (r.id === thisRowId) continue
      if (normalizeStr(r.str) !== target) continue
      const net = num(r.net_acres)
      if (r.mode === "ACQ") {
        netAcres += net
        wNri += (num(r.nri_percent) / 100) * net
        acqAcres += net
        if (net !== 0) hadBasePosition = true
      } else {
        netAcres -= net
      }
    }
  }

  const nriOwned = acqAcres > 0 ? wNri / acqAcres : baseNri
  const assigned = thisRow ? num(thisRow.nri_assigned_percent) / 100 : 0
  const orriReserved = Math.max(nriOwned - assigned, 0)

  // hasPosition mirrors the SERVER rule ("any ACQ ever"): a base inventory row
  // OR any batch ACQ for the STR grants a position, even at net 0.
  const batchAcq = target
    ? rows.some((r) => r.id !== thisRowId && r.mode === "ACQ" && normalizeStr(r.str) === target)
    : false
  const hasPosition = hadBasePosition || batchAcq

  return { naOwned: netAcres, nriOwned, orriReserved, hasPosition }
}

// A row is "empty" (skipped on submit) when every USER-editable field is blank.
// Date (defaulted to today), mode, interest_type, and expanded are ignored.
export function isRowEmpty(r: RowState): boolean {
  return (
    r.str.trim() === "" &&
    r.county.trim() === "" &&
    r.net_acres.trim() === "" &&
    r.nri_percent.trim() === "" &&
    r.nri_assigned_percent.trim() === "" &&
    r.manual_orri_percent.trim() === "" &&
    r.arti === false &&
    r.override_orri === false &&
    r.counterparty.trim() === "" &&
    r.depths.trim() === "" &&
    r.price.trim() === "" &&
    r.book_page.trim() === "" &&
    r.notes.trim() === ""
  )
}

export function updateRow(rows: RowState[], id: string, patch: Partial<RowState>): RowState[] {
  return rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
}

export interface BatchTxPayload {
  type: string
  str: string
  transaction_date: string | null
  net_acres: number
  nri: number
  orri_reserved: number
  arti?: boolean
  interest_type: string | null
  county: string | null
  depths: string | null
  price: number | null
  counterparty: string | null
  book_page: string | null
  notes: string | null
}

// Build the API payload for the non-empty rows, plus the id list so 422 errors
// (indexed over submitted rows) map back to the right grid rows.
export function buildBatchPayload(
  rows: RowState[],
  effectivePositionFor: (row: RowState) => EffectivePosition
): { payload: { transactions: BatchTxPayload[] }; submittedRowIds: string[] } {
  const submitted = rows.filter((r) => !isRowEmpty(r))
  const transactions = submitted.map<BatchTxPayload>((r) => {
    const pos = effectivePositionFor(r)
    // ARTI divestitures display Net Acres from the derived position but never
    // store it in row.net_acres — read it from the position at build time.
    const netAcres = r.mode === "DIV" && r.arti ? pos.naOwned : Number(r.net_acres)
    const base = {
      str: r.str.trim(),
      transaction_date: r.transaction_date || null,
      net_acres: netAcres,
      interest_type: r.interest_type || null,
      county: r.county || null,
      depths: r.depths || null,
      price: r.price ? Number(r.price) : null,
      counterparty: r.counterparty || null,
      book_page: r.book_page || null,
      notes: r.notes || null,
    }
    return r.mode === "ACQ"
      ? { ...base, type: "ACQ", nri: Number(r.nri_percent) / 100, orri_reserved: 0 }
      : {
          ...base,
          type: "DIV",
          nri: Number(r.nri_assigned_percent) / 100,
          orri_reserved: r.override_orri ? Number(r.manual_orri_percent) / 100 : pos.orriReserved,
          arti: r.arti,
        }
  })
  return { payload: { transactions }, submittedRowIds: submitted.map((r) => r.id) }
}

export interface BatchError {
  index: number | null
  messages: string[]
}

export function mapBatchErrorsToRows(
  errors: BatchError[],
  submittedRowIds: string[]
): Record<string, string[]> {
  const byRow: Record<string, string[]> = {}
  for (const e of errors) {
    if (e.index == null) continue
    const id = submittedRowIds[e.index]
    if (id) byRow[id] = e.messages
  }
  return byRow
}
